import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatOrderNumber, type BookingDetail, type BookingStatus } from "./BookingDetailDialog";
import { STATUS_DOT_CLASS, STATUS_LABEL, STATUS_ORDER, STATUS_ROW_CLASS, toDayKey } from "./bookingsShared";

type View = "day" | "week" | "month";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const startOfWeek = (d: Date) => {
  const r = new Date(d);
  const day = (r.getDay() + 6) % 7; // lundi = 0
  r.setDate(r.getDate() - day);
  r.setHours(0, 0, 0, 0);
  return r;
};

const addDays = (d: Date, n: number) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const fmtLongDate = (d: Date) =>
  d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const BookingsAgenda = ({
  bookings,
  unreadIds,
  onSelect,
}: {
  bookings: BookingDetail[];
  unreadIds: Set<string>;
  onSelect: (b: BookingDetail) => void;
}) => {
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [statuses, setStatuses] = useState<BookingStatus[]>(["confirmed"]);
  const [selectedDay, setSelectedDay] = useState<string | null>(() => toDayKey(new Date()));

  const visible = useMemo(
    () => bookings.filter((b) => statuses.includes(b.status)),
    [bookings, statuses]
  );

  const byDay = useMemo(() => {
    const map = new Map<string, BookingDetail[]>();
    for (const b of visible) {
      if (!b.booking_date) continue;
      const key = b.booking_date.slice(0, 10);
      const list = map.get(key) || [];
      list.push(b);
      map.set(key, list);
    }
    return map;
  }, [visible]);

  const undated = useMemo(() => visible.filter((b) => !b.booking_date), [visible]);

  const days = useMemo(() => {
    if (view === "day") return [cursor];
    if (view === "week") {
      const s = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => addDays(s, i));
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const end = addDays(startOfWeek(last), 6);
    const out: Date[] = [];
    for (let d = start; d <= end; d = addDays(d, 1)) out.push(new Date(d));
    return out;
  }, [cursor, view]);

  const shift = (dir: 1 | -1) => {
    setCursor((c) => {
      if (view === "day") return addDays(c, dir);
      if (view === "week") return addDays(c, dir * 7);
      return new Date(c.getFullYear(), c.getMonth() + dir, 1);
    });
  };

  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCursor(d);
    setSelectedDay(toDayKey(d));
  };

  const toggleStatus = (s: BookingStatus) =>
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const label =
    view === "day"
      ? fmtLongDate(cursor)
      : view === "week"
      ? `${startOfWeek(cursor).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${addDays(
          startOfWeek(cursor),
          6
        ).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`
      : cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const todayKey = toDayKey(new Date());
  const selectedList = selectedDay ? byDay.get(selectedDay) || [] : [];

  const BookingChip = ({ b }: { b: BookingDetail }) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect(b);
      }}
      className={cn(
        "w-full text-left rounded px-1.5 py-1 text-[11px] leading-tight border border-border/60 transition-colors",
        STATUS_ROW_CLASS[b.status]
      )}
    >
      <span className="flex items-center gap-1">
        {unreadIds.has(b.id) && <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />}
        <span className="font-semibold truncate">{b.name}</span>
      </span>
      {(b.pickup_location || b.dropoff_location) && (
        <span className="block truncate text-muted-foreground">
          {b.pickup_location || "?"} → {b.dropoff_location || "?"}
        </span>
      )}
    </button>
  );

  const BookingRow = ({ b }: { b: BookingDetail }) => (
    <button
      type="button"
      onClick={() => onSelect(b)}
      className={cn(
        "w-full text-left rounded-md border border-border px-3 py-2 transition-colors",
        STATUS_ROW_CLASS[b.status]
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn("w-2 h-2 rounded-full", STATUS_DOT_CLASS[b.status])} />
        <span className="font-body text-xs font-semibold">{formatOrderNumber(b.order_number)}</span>
        <span className="font-body text-sm font-medium">{b.name}</span>
        {unreadIds.has(b.id) && (
          <span className="text-[10px] font-semibold text-destructive uppercase">Nouveau</span>
        )}
        <span className="font-body text-xs text-muted-foreground ml-auto">{STATUS_LABEL[b.status]}</span>
      </div>
      <p className="font-body text-xs text-muted-foreground mt-0.5">
        {b.service}
        {b.pickup_location || b.dropoff_location
          ? ` • ${b.pickup_location || "?"} → ${b.dropoff_location || "?"}`
          : ""}
        {b.passengers != null ? ` • ${b.passengers} pax` : ""}
        {b.transfer_price != null ? ` • ${b.transfer_price} MAD` : ""}
        {` • ${b.phone}`}
      </p>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList>
            <TabsTrigger value="day" className="text-xs px-3">Jour</TabsTrigger>
            <TabsTrigger value="week" className="text-xs px-3">Semaine</TabsTrigger>
            <TabsTrigger value="month" className="text-xs px-3">Mois</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={goToday}>
            <CalendarDays className="w-4 h-4 mr-1.5" /> Aujourd'hui
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <p className="font-heading text-base font-semibold capitalize">{label}</p>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => toggleStatus(s)}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-body transition-colors",
              statuses.includes(s)
                ? "border-primary/40 bg-primary/10 text-foreground"
                : "border-border text-muted-foreground"
            )}
          >
            <span className={cn("w-2 h-2 rounded-full", STATUS_DOT_CLASS[s])} />
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {view === "day" ? (
        <div className="space-y-2">
          {(byDay.get(toDayKey(cursor)) || []).length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-8">Aucune réservation ce jour.</p>
          ) : (
            (byDay.get(toDayKey(cursor)) || []).map((b) => <BookingRow key={b.id} b={b} />)
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-[11px] font-medium text-muted-foreground py-1">
                {w}
              </div>
            ))}
            {days.map((d) => {
              const key = toDayKey(d);
              const list = byDay.get(key) || [];
              const outside = view === "month" && d.getMonth() !== cursor.getMonth();
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDay(key)}
                  className={cn(
                    "text-left rounded-md border p-1 align-top space-y-1 transition-colors",
                    view === "week" ? "min-h-[140px]" : "min-h-[96px]",
                    outside ? "opacity-45" : "",
                    selectedDay === key ? "border-primary ring-1 ring-primary/40" : "border-border",
                    key === todayKey ? "bg-muted/50" : ""
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold",
                      key === todayKey ? "bg-primary text-primary-foreground" : ""
                    )}
                  >
                    {d.getDate()}
                  </span>
                  <div className="space-y-1">
                    {list.slice(0, 3).map((b) => (
                      <BookingChip key={b.id} b={b} />
                    ))}
                    {list.length > 3 && (
                      <span className="block text-[10px] text-muted-foreground px-1">
                        +{list.length - 3}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedDay && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="font-heading text-sm font-semibold capitalize">
                {fmtLongDate(new Date(`${selectedDay}T12:00:00`))}
              </p>
              {selectedList.length === 0 ? (
                <p className="text-muted-foreground font-body text-sm">Aucune réservation ce jour.</p>
              ) : (
                selectedList.map((b) => <BookingRow key={b.id} b={b} />)
              )}
            </div>
          )}
        </>
      )}

      {undated.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="font-heading text-sm font-semibold">Sans date ({undated.length})</p>
          {undated.map((b) => (
            <BookingRow key={b.id} b={b} />
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingsAgenda;
